import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { Grid, IconButton } from "@mui/material";
import { ContentWrapper, useMemoizedTableVariables } from "@src/components";
import { ResetButton } from "@src/components/ResetButton";
import { StyledFilter, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
import { type GQLCamerasAdminQueryVariables, type GQLTrapAlarmsAdminQuery, type GQLTrapAlarmsAdminQueryVariables } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FC, type PropsWithChildren, useCallback, useContext } from "react";
import { useIntl } from "react-intl";

import { urls } from "../urls";
import { trapAlarmsQuery } from "./Table.gql";
import { StyledLink, StyledUserLink } from "./Table.sc";

interface TableProps {
    persistedStateId?: string;
}

const Table: FC<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);
    const sortApi = useTableQuerySort({ columnName: "createdAt", direction: SortDirection.DESC }, { persistedStateId });
    const filterApi = useTableQueryFilter<GQLCamerasAdminQueryVariables>({}, { pagingApi, persistedStateId });
    const intl = useIntl();

    const addAdditionalQueryVariablesFn = useCallback(() => ({ query: filterApi.current.query }), [filterApi]);

    const memoizedVariables = useMemoizedTableVariables<GQLTrapAlarmsAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLTrapAlarmsAdminQuery, GQLTrapAlarmsAdminQueryVariables>()(trapAlarmsQuery, {
        resolveTableData: ({ trapAlarmsAdmin }) => ({
            data: trapAlarmsAdmin.nodes,
            totalCount: trapAlarmsAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, trapAlarmsAdmin),
        }),
        variables: memoizedVariables,
    });

    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.trapAlarms",
        defaultMessage: "Fallenalarme",
    })}`;

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper marginVariant="table">
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.trapAlarm.filter.fulltext", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.trapAlarm.filter.fulltext.placeholder",
                                    defaultMessage: "Name, Kunde, ...",
                                })}
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.trapAlarms",
                                defaultMessage: "{count, plural, one {Fallenalarm} other {Fallenalarme}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "name",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.name", defaultMessage: "Name" }),
                                render: ({ name, id }) => (
                                    <StyledLink
                                        onClick={() => {
                                            stackApi.activatePage("edit", String(id));
                                        }}
                                    >
                                        {name}
                                    </StyledLink>
                                ),
                                renderExcel: ({ name }) => name,
                            },
                            {
                                name: "user",
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.user", defaultMessage: "Kunde" }),
                                render: ({ user: { id, firstname, lastname } }) => (
                                    <StyledUserLink to={`${urls.users}/${id}/edit`}>{`${firstname} ${lastname}`}</StyledUserLink>
                                ),
                            },
                            {
                                name: "phone",
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.phone", defaultMessage: "Telefon" }),
                                render: ({ phone }) => phone,
                            },
                            {
                                name: "pin",
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.pin", defaultMessage: "Pin" }),
                                render: ({ pin }) => pin,
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            stackApi.activatePage("edit", String(id));
                                        }}
                                    >
                                        <ArrowRight color="primary" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};

export default Table;
